import { ModelSetTransformer, ModelTransformer } from '../common/common.model';

function firstPictureLink(pictures: Picture[]): string {
    if (!pictures || pictures.length === 0) return null;
    const filtered = pictures.filter((p) => !!p.link && p.height === 166); // size from vimeo picture api
    return !filtered || filtered.length === 0 ? null : filtered[0].link;
}

interface ExtPicture {
    vimeoId: number;
    width: number;
    height: number;
    link: string;
}

interface ExtPlaylistItem {
    playlistItemId: number;
    vimeoId: number;
    name: string;
    description: string;
    duration: number;
    picturesVimeoId: number;
    displayOrder: number;
    pictures: ExtPicture[];
    playlistId: number;
}

interface ExtPlaylist {
    playlistId: number;
    vimeoId: number;
    name: string;
    description: string;
    picturesVimeoId: number;
    displayOrder: number;
    pictures: ExtPicture[];
    items: ExtPlaylistItem[];
}

export interface Picture extends ExtPicture {}

export interface PlaylistItem extends ExtPlaylistItem {
    playlistVimeoId: number;
    thumbnail: string;
    pictures: Picture[];
    lastWatched: ViewedPlaylistItem;
    attachments: Attachment[];
}

export interface Playlist extends ExtPlaylist {
    pictures: Picture[];
    thumbnail: string;
    items: PlaylistItem[];
}

class PlaylistModelTransformer
    implements ModelTransformer<Playlist, ExtPlaylist>
{
    transform(model: ExtPlaylist): Playlist {
        const transformed = JSON.parse(JSON.stringify(model)) as Playlist;
        transformed.thumbnail = firstPictureLink(transformed.pictures);
        transformed.items.forEach((i) => {
            i.playlistVimeoId = transformed.vimeoId;
            i.thumbnail = firstPictureLink(i.pictures);
        });
        transformed.items.sort((a, b) => a.displayOrder - b.displayOrder);
        return transformed;
    }
}

export class PlaylistModelSetTransformer extends ModelSetTransformer<
    Playlist,
    ExtPlaylist
> {
    protected newTransformer(): ModelTransformer<Playlist, ExtPlaylist> {
        return new PlaylistModelTransformer();
    }

    transform(model: ExtPlaylist[]): Playlist[] {
        return super
            .transform(model)
            .sort((a, b) => a.displayOrder - b.displayOrder);
    }
}

interface ExtAttachment {
    attachmentId: number;
    name: string;
    uri: string;
    displayOrder: number;
    playlistItemId: number;
}

export interface Attachment extends ExtAttachment {}

class AttachmentModelTransformer
    implements ModelTransformer<Attachment, ExtAttachment>
{
    transform(model: ExtAttachment): Attachment {
        return JSON.parse(JSON.stringify(model)) as Attachment;
    }
}

export class AttachmentModelSetTransformer extends ModelSetTransformer<
    Attachment,
    ExtAttachment
> {
    protected newTransformer(): ModelTransformer<Attachment, ExtAttachment> {
        return new AttachmentModelTransformer();
    }

    transform(model: ExtAttachment[]): Attachment[] {
        return super
            .transform(model)
            .sort((a, b) => a.displayOrder - b.displayOrder);
    }
}

export function applyLastWatched(
    viewed: ViewedPlaylistItem[],
    playlists: Playlist[],
): void {
    const items: PlaylistItem[] = playlists.reduce((p, c) => {
        p = p.concat(c.items);
        return p;
    }, []);
    items.forEach((pli) => {
        const pliViewed = viewed.filter(
            (v) =>
                v.playlistItemId === pli.playlistItemId &&
                v.playlistId === pli.playlistId,
        );
        if (pliViewed.length !== 0) {
            pli.lastWatched = pliViewed[0];
        }
    });
}

interface ExtViewedPlaylistItem {
    playlistId: number;
    playlistItemId: number;
    timestamp: string;
}

export interface ViewedPlaylistItem {
    playlistId: number;
    playlistItemId: number;
    timestamp: Date;
}

class ViewedPlaylistItemModelTransformer
    implements ModelTransformer<ViewedPlaylistItem, ExtViewedPlaylistItem>
{
    transform(model: ExtViewedPlaylistItem): ViewedPlaylistItem {
        return {
            playlistId: model.playlistId,
            playlistItemId: model.playlistItemId,
            timestamp: new Date(model.timestamp || ''),
        };
    }
}

export class ViewedPlaylistModelSetTransformer extends ModelSetTransformer<
    ViewedPlaylistItem,
    ExtViewedPlaylistItem
> {
    protected newTransformer(): ModelTransformer<
        ViewedPlaylistItem,
        ExtViewedPlaylistItem
    > {
        return new ViewedPlaylistItemModelTransformer();
    }
}

interface ExtTrackVideoEvent {
    name: string;
    playlistVimeoId: number;
    playlistItemVimeoId: number;
    position: number;
    timestamp: string;
    userId?: number;
}

export interface TrackVideoEvent {
    name: string;
    playlistVimeoId: number;
    playlistItemVimeoId: number;
    position: number;
    timestamp: Date;
    userId?: number;
}

export class TrackVideoEventTransformer
    implements ModelTransformer<TrackVideoEvent, ExtTrackVideoEvent>
{
    reverseTransform(model: TrackVideoEvent): ExtTrackVideoEvent {
        return model as any as ExtTrackVideoEvent;
    }
}
